import { UNIT_OPTIONS, urduFor } from "../../../../../Utils/staticDropdownOptions";
import { UrduInput } from "../../../../../Components/Shared/UrduInput";
import { Plus } from "lucide-react";
import ColorCard from "./ColorCard";

export default function SingleVariantPanel({ form }) {
  const {
    formData,
    handleChange,
    setFormData,
    groupColorsByVariant,
    addColor,
    validationErrors,
    clearFieldErrors,
  } = form;

  const grouped = groupColorsByVariant(formData.colors || []);
  const variantNames = Object.keys(grouped);
  const colorsInVariant =
    variantNames.length > 0 ? grouped[variantNames[0]] : [];

  const firstColorIndex =
    colorsInVariant[0]?.originalIndex !== undefined
      ? colorsInVariant[0].originalIndex
      : formData.colors?.length
        ? 0
        : undefined;
  const variantDisplayName =
    firstColorIndex !== undefined
      ? formData.colors[firstColorIndex]?.name || ""
      : "";
  const variantDisplayNameUr =
    firstColorIndex !== undefined
      ? formData.colors[firstColorIndex]?.name_ur || ""
      : "";

  // Ensure at least one color row exists for gallery / attributes
  const ensureColor = () => {
    if (!formData.colors?.length) {
      addColor();
    }
  };

  const updateSingleVariantName = (newName) => {
    setFormData((prev) => {
      if (!prev.colors?.length) return prev;
      return {
        ...prev,
        colors: prev.colors.map((c) => ({ ...c, name: newName })),
      };
    });
    if (firstColorIndex !== undefined) {
      clearFieldErrors?.(`colors.${firstColorIndex}.name`);
    }
  };

  const updateSingleVariantNameUr = (newNameUr) => {
    setFormData((prev) => {
      if (!prev.colors?.length) return prev;
      return {
        ...prev,
        colors: prev.colors.map((c) => ({ ...c, name_ur: newNameUr })),
      };
    });
  };

  return (
    <div className="space-y-5">
      <div className="pf-single-variant-box">
        <span className="pf-badge-main">Main Variant Configuration</span>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
          <div className="md:col-span-2">
            <label className="pf-label">
              Variant Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={variantDisplayName}
              onChange={(e) => updateSingleVariantName(e.target.value)}
              placeholder="Enter variant name"
              className={`pf-input ${
                firstColorIndex !== undefined &&
                validationErrors[`variant_${firstColorIndex}_name`]
                  ? "is-error"
                  : ""
              }`}
            />
            {firstColorIndex !== undefined &&
              validationErrors[`variant_${firstColorIndex}_name`] && (
                <p className="text-red-500 text-xs mt-1">
                  {validationErrors[`variant_${firstColorIndex}_name`]}
                </p>
              )}
            <UrduInput
              label="ویرینٹ کا نام"
              name="variantName_ur"
              value={variantDisplayNameUr}
              onChange={(e) => updateSingleVariantNameUr(e.target.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
          <div>
            <label className="pf-label">
              Price ({formData.currency || "PKR"})
            </label>
            <div className="relative">
              <span className="pf-input-prefix">
                {formData.currency === "$" ? "$" : "Rs."}
              </span>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                placeholder="0"
                className="pf-input pf-input-with-prefix"
              />
            </div>
          </div>
          <div>
            <label className="pf-label">Weight (KG)</label>
            <input
              type="number"
              name="weight"
              value={formData.weight}
              onChange={handleChange}
              placeholder="e.g. 25"
              className="pf-input"
            />
          </div>
          <div>
            <label className="pf-label">Unit</label>
            <select
              name="unit"
              value={formData.unit}
              onChange={(e) => {
                const unit = e.target.value;
                setFormData((prev) => ({
                  ...prev,
                  unit,
                  unit_ur: urduFor(UNIT_OPTIONS, unit),
                }));
              }}
              className="pf-select"
            >
              <option value="">Select Unit</option>
              {UNIT_OPTIONS.map((o) => (
                <option key={o.en} value={o.en}>
                  {o.enLabel}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {validationErrors.variants && (
        <p className="text-red-500 text-xs">{validationErrors.variants}</p>
      )}

      {colorsInVariant.length === 0 ? (
        <button
          type="button"
          onClick={ensureColor}
          className="pf-dashed-btn"
        >
          + Add Color &amp; Gallery
        </button>
      ) : (
        <div className="space-y-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-brand-primary mb-0.5">
              Colors &amp; Gallery
            </p>
            <h4 className="text-sm font-bold text-gray-900">
              Add product colors and images
            </h4>
            <p className="pf-step-hint">
              First pick a color, then upload images (450 × 450 px)
            </p>
          </div>
          {colorsInVariant.map((colorData, colorIndexInVariant) => {
            const actualIndex =
              colorData.originalIndex !== undefined
                ? colorData.originalIndex
                : colorIndexInVariant;
            return (
              <ColorCard
                key={actualIndex}
                form={form}
                actualIndex={actualIndex}
                colorIndexInVariant={colorIndexInVariant}
                colorsInVariantLength={colorsInVariant.length}
                variantName={variantNames[0]}
                layout="single"
              />
            );
          })}
          {formData.colors.length < 9999 && (
            <div className="pf-section-actions">
              <button
                type="button"
                onClick={() => addColor()}
                className="pf-btn-add-item"
              >
                <Plus size={16} strokeWidth={2.5} />
                Add Another Color
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
